<template>
  <b-modal
    id="bulk-import-employees-modal"
    centered
    title="Upload File"
    @show="initForm"
    footer-class="px-5">
    <b-form>
      <b-container fluid id="upload-file-container">
        <div id="bulk-employee-upload-input-container" class="p-3 mb-2" v-bind:class="{ 'hide-label': file }">
          <b-form-file v-model="file" ref="bulkEmployeeFileInput" accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" placeholder="Drop your file here " @change="validateFile"></b-form-file>
        </div>
        <div :class="['my-4 justify-content-between', { 'd-none': !file, 'd-flex': file }]">
          <div class="d-inline-flex align-items-center file-name-container">
            <img class="d-inline-block upload-file-icon" width="16" height="20" src="@/assets/images/icons/ic-upload-file.svg" alt="">
            <div class="ml-2 d-inline-block upload-file-name" v-if="file != null">
              {{ file.name }}
            </div>
          </div>
          <div class="d-inline-block cancel-file-upload-container">
            <span>{{ formatBytes(file) }}</span>
            <img width="15" height="15" src="@/assets/images/icons/cancel-file-upload.svg" alt="" @click="cancelFileUpload()" class="cancel-file-upload-icon">
          </div>
        </div>
        <img class="d-inline-block upload-file-folder-icon position-absolute" width="42" height="42" src="@/assets/images/icons/ic-folder.svg" alt="">
        <label :class="['shown-label', { 'd-none': !file }]">Drop your file here or <span>browse</span></label>
        <b-form-invalid-feedback :force-show="hasErrors('file')">{{ errorMessage('file') }}</b-form-invalid-feedback>
        <BrandBranches
          @applyBrandsAndBranches="applyBrandsAndBranches"
          errorKey="stores"
          :brand-branch="brand_branch"
          class="mt-3"
          is-single
        />
        <b-form-invalid-feedback :force-show="hasErrors('brand_uuid')">{{ errorMessage('brand_uuid') }}</b-form-invalid-feedback>
        <b-form-invalid-feedback :force-show="hasErrors('branch_uuid')">{{ errorMessage('branch_uuid') }}</b-form-invalid-feedback>
      </b-container>
    </b-form>
    <template slot="modal-footer">
      <b-button
        pill block
        variant="primary"
        :disabled="isUploadFileButtonDisabled"
        @click="uploadBulkEmployees">
        Upload File
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapState } from "vuex";
import _ from "lodash";

import BrandBranches from "@/components/store/BrandBranches";

export default {
    components: { BrandBranches },
    data() {
        return {
            file: null,
            brand_branch: {},
            compiledErrors: {}
        };
    },
    computed: {
        ...mapState("employees", ["error", "saving"]),
        ...mapState("analytics", ["isSegmentAnalyticsReady"]),
        isUploadFileButtonDisabled: {
            get() {
                return (
                    !this.file || _.isEmpty(this.brand_branch) || this.saving || !_.isEmpty(this.error)
                );
            },
            set(status) {
                return status;
            }
        }
    },
    methods: {
        formatBytes(file, decimals = 2) {
            if (file != null) {
                if (file.size === 0) return "0 Bytes";

                const k = 1024;
                const dm = decimals < 0 ? 0 : decimals;
                const sizes = [
                    "Bytes",
                    "KB",
                    "MB",
                    "GB",
                    "TB",
                    "PB",
                    "EB",
                    "ZB",
                    "YB"
                ];

                const i = Math.floor(Math.log(file.size) / Math.log(k));
                const size = parseFloat(
                    (file.size / Math.pow(k, i)).toFixed(dm)
                );

                return `${size} ${sizes[i]}`;
            }
        },
        cancelFileUpload() {
            this.$refs.bulkEmployeeFileInput.reset();
            this.$store.commit("employees/error", null);
        },
        applyBrandsAndBranches(brandAndBranches) {
            this.brand_branch = JSON.parse(JSON.stringify(brandAndBranches));
        },
        initForm() {
            this.brand_branch = {};
            this.$refs.bulkEmployeeFileInput.reset();
            this.$store.commit("employees/error", null);
        },
        async uploadBulkEmployees() {
            const fileInBase64 = await this.getBase64(this.file);
            const payload = {
                file: fileInBase64 ? fileInBase64.split("base64,").pop() : null,
                brand_uuid: this.brand_branch.brand_uuid,
                branch_uuid: this.brand_branch.branch_uuid
            };

            await this.$store.dispatch(
                "employees/bulkImportEmployees",
                payload
            );

            if (!this.error || _.isEmpty(this.error)) {
                if (this.isSegmentAnalyticsReady) {
                    window.analytics.track("Import Employee", {
                        file_name: this.file.name
                    });
                }

                this.$root.$emit(
                    "bv::hide::modal",
                    "bulk-import-employees-modal"
                );
            }
        },
        async getBase64(file) {
            return new Promise(function(resolve, reject) {
                var reader = new FileReader();
                reader.onload = function() {
                    resolve(reader.result);
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        },
        hasErrors(key) {
            if (this.error) return key in this.error;
        },
        errorMessage(key) {
            if (this.error && this.error[key]) return this.error[key];
        },
        validateFile(e) {
            let file;
            if (e.type == 'change') {
                file = e.target.files[0];
                this.filename = file.name;
            } else if (e.type == 'drop') {
                file = e.dataTransfer.files[0];
                this.filename = file.name;
            }

            this.$store.commit("employees/error", null);

            if (
                !["application/vnd.ms-excel", ".csv", "text/csv"].includes(
                    file.type
                )
            ) {
                this.$store.commit("employees/error", {
                    file: "Invalid file format, only CSV file is accepted."
                });
            } else if (
                file.size > 5000000
            ) {
                this.$store.commit("employees/error", {
                    file: "File size exceeds the limit. Maximum size: 5MB"
                });
            }
        }
    }
};
</script>

<style lang="scss">
#bulk-employee-upload-input-container {
    border: 1.5px dashed #d9dde7;
    border-radius: 10px;
    height: 70px;
    .custom-file-label {
        border: 0px;
        font-size: 14px;
        margin-left: 80px;
        width: 210px;
        margin-right: auto;
    }
    .custom-file-input {
        position: relative;
        top: -17px;
        height: 70px;
    }
    .custom-file-input:lang(en) ~ .custom-file-label::after {
        content: "browse";
        color: #5181ff;
        background-color: #ffffff;
        font-family: MainFontBold, sans-serif !important;
        font-weight: normal;
        font-size: 14px;
        border-left: 0px;
        padding-left: 0px;
    }
    &.hide-label {
        .custom-file-label {
            display: none !important;
        }
    }
}
</style>

<style lang="scss" scoped>
#upload-file-container {
    .upload-file-folder-icon {
        z-index: 9;
        left: 125px;
        top: 26px;
    }
    .upload-file-name {
        font-family: MainFontRegular, sans-serif;
        font-size: 14px;
        color: #68737d;
        width: 200px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .cancel-file-upload-container {
        padding-left: 10px;
        span {
            font-family: MainFontRegular, sans-serif;
            font-size: 14px;
            color: #68737d;
            width: 200px;
            margin-right: 10px;
        }
        .cancel-file-upload-icon {
            cursor: pointer;
        }
    }
    .shown-label {
        position: absolute;
        top: 37px;
        left: 175px;
        z-index: 9;
        font-size: 14px;
        font-family: MainFontRegular, sans-serif !important;
        color: #495057;
        span {
            font-family: MainFontBold, sans-serif !important;
            color: #5181ff;
            font-weight: normal;
            font-size: 14px;
        }
    }
}
</style>
